import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useScaffoldProcess } from "@/api/mutations/processes";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// #282 (UC-6): Prozess anlegen = Scaffold (Repo + schema-valides Gerüst + Registry).
// Bewusst OHNE Agent-Feld: der ausführende Agent wird pro INSTANZ gebunden (Nachtrag 2).
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

export function ScaffoldProcessDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const scaffold = useScaffoldProcess();
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const idValid = SLUG_RE.test(id);
  const canSubmit = idValid && name.trim().length > 0 && !scaffold.isPending;

  const reset = () => {
    setId("");
    setName("");
    setDescription("");
    scaffold.reset();
  };

  const submit = () => {
    scaffold.mutate(
      { id, name: name.trim(), ...(description.trim() ? { description: description.trim() } : {}) },
      {
        onSuccess: (p) => {
          toast.success(`Prozess „${p.name}" angelegt`);
          reset();
          onClose();
          navigate(`/processes/${p.id}`);
        },
        onError: (err) => {
          const msg =
            err instanceof ApiError && err.status === 409
              ? "Id ist bereits vergeben"
              : "Prozess konnte nicht angelegt werden";
          toast.error(msg);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Prozess anlegen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="process-id">Id (Slug)</Label>
            <Input
              id="process-id"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="umsatzbuchung"
              data-testid="scaffold-process-id"
            />
            {id.length > 0 && !idValid && (
              <p className="text-[12px] text-danger">Nur Kleinbuchstaben, Ziffern und Bindestriche (max. 64).</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="process-name">Name</Label>
            <Input
              id="process-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Umsatzbuchung"
              data-testid="scaffold-process-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="process-description">Beschreibung (optional)</Label>
            <Textarea
              id="process-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <p className="text-[12px] leading-relaxed text-text3">
            Legt ein governed Repository mit einem schema-validen Gerüst an. Der Inhalt wird danach
            unter „Repositories" bearbeitet (Entwurf → Freigabe → Version). Der ausführende Agent
            wird erst pro Instanz gebunden.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }}>
            Abbrechen
          </Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="scaffold-process-submit">
            {scaffold.isPending ? "Lege an…" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
